import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, createFileRoute, useParams } from '@tanstack/react-router';
import { Spinner } from '@volvo/vcdk-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useLayout from '@/hooks/useLayout';
import { getAllFleetUnitsQueryOptions } from '@/service/vpc/queryOptions';

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/dashboard')({
  component: DashboardLayout,
  errorComponent: ErrorBoundary,
});

function DashboardLayout() {
  const { fleetId } = useParams({
    from: '/_authenticated/_account/fleets/$fleetId',
  });
  const { fleetAccountUnits, isLoading } = useFleetAccount();

  const { data: fleetUnits, isLoading: fleetUnitsLoading } = useQuery(getAllFleetUnitsQueryOptions({ fleetId }));

  const fleetUnitsIds = fleetUnits?.items.map((unit) => Number(unit.endCustomerUnitId));
  const filteredUnits = fleetAccountUnits?.filter((unit) => fleetUnitsIds?.includes(unit.endCustomerUnitId));

  // Both queries must finish before filteredUnits is trustworthy — using && here let
  // whichever query resolved first mask the other still being in flight.
  const unitsLoading = isLoading || fleetUnitsLoading;

  const { canShowDashboard } = useLayout({
    units: filteredUnits,
    unitsLoading,
  });

  // canShowDashboard is also false while still loading — wait for data before deciding to
  // redirect, otherwise every reload briefly reports false and bounces the user to /units.
  if (unitsLoading) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner size="medium" />
      </div>
    );
  }

  if (!canShowDashboard) {
    return <Navigate to="/fleets/$fleetId/units" params={{ fleetId }} replace />;
  }

  return <Outlet />;
}
